<template>
	<div class="academy-chat-widget" :class="{ 'academy-chat-widget--open': isOpen }">
		<button
			type="button"
			class="academy-chat-widget__toggle"
			:aria-label="isOpen ? 'Закрыть AI-ассистент курса' : 'Открыть AI-ассистент курса (тест)'"
			@click="isOpen = !isOpen"
		>{{ isOpen ? '×' : '💬' }}</button>

		<div v-if="isOpen" class="academy-chat-widget__panel">
			<div class="academy-chat-widget__header">AI-ассистент курса (тест)</div>

			<div ref="messagesEl" class="academy-chat-widget__messages">
				<p v-if="!messages.length" class="academy-chat-widget__hint">
					Это тестовая функциональность, не финальная. Спросите что-нибудь про текущий курс.
				</p>
				<div
					v-for="(message, index) in messages"
					:key="index"
					class="academy-chat-widget__message"
					:class="`academy-chat-widget__message--${message.role}`"
				>{{ message.content }}</div>
				<div v-if="isLoading" class="academy-chat-widget__message academy-chat-widget__message--assistant academy-chat-widget__message--pending">…</div>
			</div>

			<form class="academy-chat-widget__form" @submit.prevent="send">
				<input v-model.trim="draft" type="text" placeholder="Спросите про курс" :disabled="isLoading">
				<button type="submit" :disabled="isLoading || !draft">Отправить</button>
			</form>
		</div>
	</div>
</template>

<script>
import axios from "axios";

// FT-018: тестовый AI-чат-виджет, виден только staff/admin (гейт на сервере — в layout и в
// Academy::ChatWidgetController). Stateless-per-request: полная история пересылается на каждый
// вызов (SOL-09 design.md) — сервер попутно всё равно всё логирует в ChatWidgetConversation.
export default {
	name: "AcademyChatWidget",

	props: {
		course_id: {
			type: Number,
			required: true
		},
		lecture_pos: {
			type: Number,
			required: false,
			default: null
		},
		message_url: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			isOpen: false,
			draft: "",
			messages: [],
			conversationId: null,
			isLoading: false
		};
	},

	methods: {
		async send() {
			const text = this.draft.trim();
			if (!text || this.isLoading) return;

			const history = this.messages.map((message) => ({ role: message.role, content: message.content }));
			this.messages.push({ role: "user", content: text });
			this.draft = "";
			this.isLoading = true;
			this.scrollToBottom();

			try {
				const response = await axios.request({
					url: this.message_url,
					method: "POST",
					headers: {
						"X-Requested-With": "XMLHttpRequest",
						"X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
					},
					data: {
						message: text,
						lecture_pos: this.lecture_pos,
						conversation_id: this.conversationId,
						history
					}
				});
				this.conversationId = response.data.conversation_id;
				this.messages.push({ role: "assistant", content: response.data.reply });
			} catch (e) {
				this.messages.push({ role: "assistant", content: "Не удалось отправить сообщение. Попробуйте ещё раз." });
			} finally {
				this.isLoading = false;
				this.scrollToBottom();
			}
		},

		scrollToBottom() {
			this.$nextTick(() => {
				const el = this.$refs.messagesEl;
				if (el) el.scrollTop = el.scrollHeight;
			});
		}
	}
};
</script>

<style scoped>
.academy-chat-widget {
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 1000;
}

.academy-chat-widget__toggle {
	width: 56px;
	height: 56px;
	border: none;
	border-radius: 50%;
	background: #6b4dff;
	color: #fff;
	font-size: 24px;
	box-shadow: 0 8px 22px rgba(107, 77, 255, 0.35);
	cursor: pointer;
}

.academy-chat-widget__panel {
	position: absolute;
	right: 0;
	bottom: 68px;
	display: flex;
	flex-direction: column;
	width: 340px;
	max-height: 460px;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 12px 34px rgba(22, 33, 58, 0.22);
	overflow: hidden;
}

.academy-chat-widget__header {
	padding: 14px 16px;
	background: #6b4dff;
	color: #fff;
	font-weight: 700;
	font-size: 14px;
}

.academy-chat-widget__messages {
	flex: 1;
	min-height: 160px;
	max-height: 320px;
	padding: 12px;
	overflow-y: auto;
}

.academy-chat-widget__hint {
	margin: 0;
	font-size: 13px;
	color: #6e7990;
}

.academy-chat-widget__message {
	max-width: 85%;
	margin-bottom: 8px;
	padding: 8px 12px;
	border-radius: 12px;
	font-size: 14px;
	line-height: 1.4;
	white-space: pre-wrap;
}

.academy-chat-widget__message--user {
	margin-left: auto;
	background: #f4f7fb;
	color: #15213b;
}

.academy-chat-widget__message--assistant {
	margin-right: auto;
	background: #f5f2ff;
	color: #15213b;
}

.academy-chat-widget__message--pending {
	opacity: 0.6;
}

.academy-chat-widget__form {
	display: flex;
	gap: 8px;
	padding: 12px;
	border-top: 1px solid #e6ecf4;
}

.academy-chat-widget__form input {
	flex: 1;
	padding: 8px 12px;
	border: 1px solid #e6ecf4;
	border-radius: 10px;
	font-size: 14px;
}

.academy-chat-widget__form button {
	padding: 8px 14px;
	border: none;
	border-radius: 10px;
	background: #6b4dff;
	color: #fff;
	font-weight: 700;
	cursor: pointer;
}

.academy-chat-widget__form button:disabled {
	opacity: 0.5;
	cursor: default;
}
</style>
